<template>
  <span class="report-scheduled-report-editor">
    <ReportScheduledReportDialog
      v-if="openDialog"
      show
      :custom-message-allowed="customMessageAllowed"
      :time-zone="timeZone"
      :time-zone-id="timeZoneId"
      :saving="saving"
      :saving-preview="savingPreview"
      :default-message="defaultMessage"
      :default-subject="defaultSubject"
      :message-maxlength="messageMaxlength"
      :errors="errors"
      :filter-count="filterCount"
      :filters-detailed="filtersDetailed"
      :format-option-ids="formatOptionIds"
      :report-name="reportName"
      :delivery-frequency="weeklyReport"
      :scheduled-reports-improvements="scheduledReportsImprovements"
      @save="onSave"
      @preview="onPreview"
      @close="onCloseDialog"
    />
    <a
      v-tooltip="isDisabled ? { placement: 'bottom' } : false"
      href="#"
      class="btn btn-naked"
      :title="tooltip"
      :disabled="isDisabled"
      @click.prevent="onShowDialog"
    >
      <i class="hi hi-time hi-16" />
      Schedule
    </a>
  </span>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex';
import { SCHEDULED_REPORT_FORMATS, SCHEDULED_REPORT_DATE_RANGES, SCHEDULED_REPORT_INTERVALS } from '~/app_constants';

const ERROR_MESSAGE = 'Oops, something went wrong';

export default {
  props: {
    filterCount: {
      type: Number,
    },
    filtersDetailed: {
      type: Array,
      default: () => [],
    },
    formatOptionIds: {
      type: Array,
      default() {
        return SCHEDULED_REPORT_FORMATS.map(({ id }) => id);
      },
    },
    showScheduledReportDialog: {
      type: Boolean,
      default: false,
    },
    scheduledReportsImprovements: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showDialog: false,
      disabled: false,
      errors: null,
      saving: false,
      savingPreview: false,
    };
  },
  computed: {
    ...mapState({
      originalShowScheduledReportAfterLoading: 'show_scheduled_report_after_loading',
      reportName: ({ report_name }) => report_name,
      defaultMessage: ({ scheduled_report_default_message }) => scheduled_report_default_message,
      defaultSubject: ({ scheduled_report_default_subject }) => scheduled_report_default_subject,
      tooltip() {
        if (this.isDisabled) {
          return 'The number of scheduled reports has reached the plan limit';
        }
      },
      isDisabled() {
        return this.limitReached || this.disabled;
      },
      organization: ({ organization }) => organization,
      limitReached: ({ limit_reached }) => limit_reached,
      messageMaxlength: ({ scheduled_report_message_maxlength }) => scheduled_report_message_maxlength,
    }),
    ...mapGetters(['scheduleReportToastClickSource']),
    timeZone() {
      return this.organization.time_zone_name;
    },
    timeZoneId() {
      return this.organization.time_zone_id;
    },
    startWeekOn() {
      return this.organization.start_week_on;
    },
    customMessageAllowed() {
      return this.organization.plan_paid;
    },
    openDialog() {
      return this.showScheduledReportDialog || this.showDialog;
    },
    weeklyReport() {
      return this.originalShowScheduledReportAfterLoading ? 'weekly' : null;
    },
  },
  methods: {
    ...mapActions(['analyticsTrackReport']),
    onShowDialog() {
      if (this.isDisabled) {
        return false;
      }

      this.showDialog = true;
      this.analyticsTrackReport({ event: 'Report schedule opened' });
    },
    onCloseDialog(value) {
      this.showDialog = false;
      this.errors = null;
      this.$emit('close', value);
    },
    onSave(scheduledReport) {
      this.saving = true;

      const success = (response) => {
        if (response.success) {
          if (this.scheduledReportsImprovements) {
            return window.location.assign(response.scheduled_report_index_url);
          }

          this.$appMessage('Your report has been scheduled');

          this.onCloseDialog();
          if (this.disabled !== response.limit_reached) {
            this.disabled = response.limit_reached;
          }

          this.analyticsTrackSave(scheduledReport);
        } else {
          this.$appMessage(ERROR_MESSAGE);
          this.errors = response.errors;
        }
      };
      const error = () => {
        this.$appMessage(ERROR_MESSAGE);
      };
      const fin = () => {
        this.saving = false;
      };
      const track = {};

      if (this.scheduleReportToastClickSource) {
        track.click_source = this.scheduleReportToastClickSource;
      }
      this.$store.dispatch('createScheduledReport', { scheduledReport, track }).then(success, error).then(fin);
    },
    onPreview(scheduledReport) {
      this.saving = true;

      const success = (response) => {
        if (response.success) {
          this.$appMessage(`Preview email sent to ${response.email}`);
        } else {
          this.$appMessage(ERROR_MESSAGE);
          this.errors = response.errors;
        }
      };
      const error = () => {
        this.$appMessage(ERROR_MESSAGE);
      };
      const fin = () => {
        this.saving = false;
      };

      this.$store
        .dispatch('previewScheduledReport', { scheduledReport, savedReportFilter: this.savedReportFilter })
        .then(success, error)
        .then(fin);
    },
    analyticsTrackSave(scheduledReport) {
      this.analyticsTrackReport({
        event: 'Report scheduled',
        groupProps: true,
        filtersProps: true,
        colsProps: true,
        props: {
          export_value: SCHEDULED_REPORT_FORMATS.find(({ id }) => scheduledReport.format === id)?.key,
          date_range_preset_value: SCHEDULED_REPORT_DATE_RANGES.find(({ id }) => scheduledReport.date_range === id)
            ?.text,
          frequency_value: SCHEDULED_REPORT_INTERVALS.find(({ id }) => scheduledReport.interval === id)?.text,
        },
      });
    },
  },
};
</script>
